import axios from 'axios';
import authService from '../firebase/authService';

// Configure axios defaults
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://hypotactic-nonsystematical-eleonore.ngrok-free.dev/';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor for authentication and logging
api.interceptors.request.use(
  async (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);

    // Add authentication token if user is logged in
    const user = authService.getCurrentUser();
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        console.log('Added auth token to request');
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    } else {
      console.log('No authenticated user found');
    }

    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with retry logic and better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error('API Error:', error);

    // Enhanced error classification
    if (error.response?.status === 401) {
      console.log('Authentication failed, user needs to login');
      throw new Error('Authentication required. Please sign in to continue.');
    }

    if (error.response?.status === 402) {
      // Extract limit information from error response headers
      const errorData = error.response.data;
      const headers = error.response.headers;
      const limitError = new Error(errorData.detail || errorData.message || 'Payment required to access this feature');
      limitError.isLimitError = true;
      limitError.limitInfo = {
        feature: headers['x-feature'] || errorData.feature,
        limit: headers['x-limit'] === 'N/A' || headers['x-limit'] === 'None' ? null : parseInt(headers['x-limit']) || errorData.limit,
        used: parseInt(headers['x-used']) || errorData.used || 0,
        remaining: parseInt(headers['x-remaining']) || errorData.remaining || 0,
        message: errorData.detail || errorData.message
      };
      throw limitError;
    }

    if (error.response?.status === 429) {
      // Rate limiting - suggest retry with backoff
      throw new Error('Too many requests. Please wait a moment and try again.');
    }

    if (error.response?.status === 503) {
      // Service unavailable - temporary issue
      throw new Error('Service temporarily unavailable. Please try again in a few minutes.');
    }

    if (error.response?.status >= 500) {
      // Server errors - retry once after delay
      if (!error.config._retry) {
        error.config._retry = true;
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        return api.request(error.config);
      }
      throw new Error('Server error. Please try again later.');
    }

    // Network errors - retry with exponential backoff
    if (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') {
      if (!error.config._retry) {
        error.config._retry = true;
        const delay = Math.min(1000 * Math.pow(2, error.config._retryCount || 0), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
        error.config._retryCount = (error.config._retryCount || 0) + 1;
        return api.request(error.config);
      }
      throw new Error('Network connection failed. Please check your internet connection and try again.');
    }

    // Parse error details from response
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }

    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    // Default error message
    throw new Error(error.message || 'An unexpected error occurred');
  }
);

// Career API functions
export const careerAPI = {
  // Suggest career paths (no auth required)
  suggestCareers: async (requestData) => {
    try {
      const response = await api.post('/api/suggest-careers', requestData, {
        timeout: 60000  // 60 second timeout
      });
      return response.data;
    } catch (error) {
      console.error('Error suggesting careers:', error);
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  },
  // Get dynamic skills taxonomy
  getSkillsTaxonomy: async (industry, role, experience) => {
    const response = await api.get('/api/skills-taxonomy', {
      params: { industry, role, experience }
    });
    return response.data;
  },

  // Preview career paths mid-flow (lightweight, no auth required)
  previewCareerPaths: async (skills, experienceLevel, currentRole = null, industries = null) => {
    try {
      const response = await api.post('/api/preview-career-paths', {
        skills,
        experience_level: experienceLevel,
        current_role: currentRole,
        industries
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing career paths:', error);
      // Return fallback on error
      return {
        potential_paths: ['Loading...'],
        message: 'Continue to see your career matches!',
        confidence: 'low'
      };
    }
  },

  // Refine career suggestions (no auth required)
  refineSuggestions: async (profileData, followUpAnswers) => {
    try {
      const response = await api.post('/api/refine-suggestions', {
        profile: profileData,
        follow_up_answers: followUpAnswers
      });
      return response.data;
    } catch (error) {
      console.error('Error refining suggestions:', error);
      throw error;
    }
  },

  // Analyze resume (auth optional but recommended)
  analyzeResume: async (resumeFile) => {
    const formData = new FormData();
    formData.append('resume_file', resumeFile);

    try {
      const response = await api.post('/api/analyze-resume', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error analyzing resume:', error);
      throw error;
    }
  },

  // Generate a massive markdown tutorial on demand for a specific roadmap task
  generateTaskDeepDive: async (taskData) => {
    try {
      const response = await api.post('/api/generate-task-deep-dive', taskData, {
        timeout: 125000 // Complex generation needs higher timeout
      });
      return response.data;
    } catch (error) {
      console.error('Error generating deep dive:', error);
      throw error;
    }
  },

  // Generate career roadmap (requires auth and subscription OR payment)
  // orderId is optional - if user has subscription, orderId is not needed
  generateRoadmap: async (profileData, careerPath, orderId = null, refinementAnswers = null) => {
    try {
      const endpoint = refinementAnswers ? '/api/generate-personalized-roadmap' : '/api/generate-roadmap';
      const payload = refinementAnswers
        ? {
          profile: profileData,
          career_path: careerPath,
          refinement_answers: refinementAnswers
        }
        : {
          profile: profileData,
          selected_career_path: careerPath,
          timeline_months: 12
        };

      // Only include order_id if provided (for one-time payments)
      if (orderId) {
        if (refinementAnswers) {
          payload.order_id = orderId;
        } else {
          payload.order_id = orderId;
        }
      }

      const response = await api.post(endpoint, payload, {
        timeout: 120000 // Extended timeout (120s) for large GP-4 roadmap generations
      });
      console.log('API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error generating roadmap:', error);
      throw error;
    }
  },

  // Get career path preview (no auth required)
  getCareerPathPreview: async (careerPath) => {
    try {
      const response = await api.get(`/api/career-paths/${encodeURIComponent(careerPath)}/preview`);
      return response.data;
    } catch (error) {
      console.error('Error getting career path preview:', error);
      throw error;
    }
  },

  // Get user roadmaps (requires auth)
  getUserRoadmaps: async (activeOnly = false) => {
    try {
      const response = await api.get('/api/user-roadmaps', {
        params: { active_only: activeOnly }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting user roadmaps:', error);
      throw error;
    }
  },

  // SSE for streaming career suggestions
  streamSuggestCareers: async (profile, onUpdate, onError, onComplete) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stream-suggest-careers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profile })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              onUpdate(data);
            } catch (e) {
              console.warn('Failed to parse SSE data:', line);
            }
          } else if (line.startsWith('event: complete')) {
            // Next line should be the complete data
            continue;
          } else if (line.startsWith('data: ') && line.includes('career_paths')) {
            try {
              const data = JSON.parse(line.slice(6));
              onComplete(data);
            } catch (e) {
              console.warn('Failed to parse complete data:', line);
            }
          }
        }
      }
    } catch (error) {
      console.error('SSE Error:', error);
      onError(error);
    }
  },

  // SSE for streaming roadmap generation
  streamGenerateRoadmap: async (profile, career_path, refinement_answers, onUpdate, onError, onComplete) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stream-roadmap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile,
          career_path,
          refinement_answers
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              onUpdate(data);
            } catch (e) {
              console.warn('Failed to parse SSE data:', line);
            }
          } else if (line.startsWith('event: complete')) {
            // Next line should be the complete data
            continue;
          } else if (line.startsWith('data: ') && line.includes('weeks')) {
            try {
              const data = JSON.parse(line.slice(6));
              onComplete(data);
            } catch (e) {
              console.warn('Failed to parse complete data:', line);
            }
          }
        }
      }
    } catch (error) {
      console.error('SSE Error:', error);
      onError(error);
    }
  },

  // Update task progress (requires auth)
  updateTaskProgress: async (roadmapId, weekIndex, taskIndex, completed) => {
    try {
      const response = await api.post('/api/update-task-progress', {
        roadmap_id: roadmapId,
        week_index: weekIndex,
        task_index: taskIndex,
        completed: completed
      });
      return response.data;
    } catch (error) {
      console.error('Error updating task progress:', error);
      throw error;
    }
  },

  // Get user analytics (requires auth)
  getUserAnalytics: async () => {
    try {
      const response = await api.get('/api/user-analytics');
      return response.data;
    } catch (error) {
      console.error('Error getting user analytics:', error);
      throw error;
    }
  },

  // Create user profile (requires auth)
  createUserProfile: async (userData) => {
    try {
      const response = await api.post('/api/create-user-profile', {
        user_data: userData
      });
      return response.data;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  },

  // Get user profile (requires auth)
  getUserProfile: async () => {
    try {
      const response = await api.get('/api/user-profile');
      return response.data;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  },

  // Delete roadmap (requires auth)
  deleteRoadmap: async (roadmapId) => {
    try {
      const response = await api.delete(`/api/roadmap/${roadmapId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting roadmap:', error);
      throw error;
    }
  },

  // Generate resume improvement report (requires auth)
  generateResumeImprovementReport: async (profile, resumeText) => {
    try {
      const response = await api.post('/api/generate-resume-improvements', {
        profile: profile,
        resume_text: resumeText
      });
      return response.data;
    } catch (error) {
      console.error('Error generating resume improvement report:', error);
      throw error;
    }
  },

  // Generate resume improvement report with file upload (requires auth)
  generateResumeImprovementReportWithFile: async (formData) => {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const response = await api.post('/api/generate-resume-improvements', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error generating resume improvement report:', error);
      throw error;
    }
  },

  // Generate portfolio projects (requires auth)
  generatePortfolioProjects: async (profile, careerPath) => {
    try {
      const response = await api.post('/api/generate-portfolio-projects', {
        profile: profile,
        career_path: careerPath
      });
      return response.data;
    } catch (error) {
      console.error('Error generating portfolio projects:', error);
      throw error;
    }
  },

  // Get user's saved portfolio projects (requires auth)
  getUserPortfolioProjects: async (careerPath = null) => {
    try {
      const params = careerPath ? { career_path: careerPath } : {};
      const response = await api.get('/api/user-portfolio-projects', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting portfolio projects:', error);
      throw error;
    }
  },

  // Delete a saved portfolio project (requires auth)
  deletePortfolioProject: async (portfolioId) => {
    try {
      const response = await api.delete(`/api/portfolio-project/${portfolioId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting portfolio project:', error);
      throw error;
    }
  },

  // Generate interview questions (requires auth)
  generateInterviewQuestions: async (careerPath, difficulty) => {
    try {
      const response = await api.post('/api/generate-interview-questions', {
        career_path: careerPath,
        difficulty: difficulty
      });
      return response.data;
    } catch (error) {
      console.error('Error generating interview questions:', error);
      throw error;
    }
  },

  // Evaluate interview answer (requires auth)
  evaluateInterviewAnswer: async (question, userAnswer, sampleAnswer) => {
    try {
      const response = await api.post('/api/evaluate-interview-answer', {
        question: question,
        user_answer: userAnswer,
        sample_answer: sampleAnswer
      });
      return response.data;
    } catch (error) {
      console.error('Error evaluating interview answer:', error);
      throw error;
    }
  }
};

// Payment API functions
export const paymentAPI = {
  // Create payment link (legacy - for one-time purchases)
  createPaymentLink: async (name, email, phone = null) => {
    try {
      const response = await api.post('/api/create-payment-link', {
        name,
        email,
        phone
      });
      return response.data;
    } catch (error) {
      console.error('Error creating payment link:', error);
      throw error;
    }
  },

  // Create subscription payment
  createSubscriptionPayment: async (tier, billingCycle, name, email, phone = null) => {
    try {
      const response = await api.post('/api/create-subscription-payment', {
        tier,
        billing_cycle: billingCycle,
        name,
        email,
        phone
      });
      return response.data;
    } catch (error) {
      console.error('Error creating subscription payment:', error);
      throw error;
    }
  },

  // Get subscription plans
  getSubscriptionPlans: async () => {
    try {
      const response = await api.get('/api/subscription-plans');
      return response.data;
    } catch (error) {
      console.error('Error getting subscription plans:', error);
      throw error;
    }
  },

  // Get user subscription (requires auth)
  getUserSubscription: async () => {
    try {
      const response = await api.get('/api/user-subscription');
      return response.data;
    } catch (error) {
      console.error('Error getting user subscription:', error);
      throw error;
    }
  },

  // Get user usage stats (requires auth)
  getUserUsageStats: async () => {
    try {
      const response = await api.get('/api/user-usage-stats');
      return response.data;
    } catch (error) {
      console.error('Error getting user usage stats:', error);
      throw error;
    }
  },

  // Cancel subscription (requires auth)
  cancelSubscription: async (subscriptionId) => {
    try {
      const response = await api.post('/api/cancel-subscription', {
        subscription_id: subscriptionId
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  // Verify payment (legacy)
  verifyPayment: async (orderId) => {
    try {
      const response = await api.get(`/api/verify-payment/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  },

  // Get payment status (legacy)
  getPaymentStatus: async (orderId) => {
    try {
      const response = await api.get(`/api/payment-status/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting payment status:', error);
      throw error;
    }
  },

  // Create test payment (for development)
  createTestPayment: async (name, email, phone = null) => {
    try {
      const response = await api.post('/api/test-payment', {
        name,
        email,
        phone
      });
      return response.data;
    } catch (error) {
      console.error('Error creating test payment:', error);
      throw error;
    }
  },

  // Activate subscription after payment
  activateSubscription: async (orderId, tier, billingCycle = 'monthly') => {
    try {
      const response = await api.post('/api/activate-subscription', {
        order_id: orderId,
        tier: tier,
        billing_cycle: billingCycle
      });
      return response.data;
    } catch (error) {
      console.error('Error activating subscription:', error);
      throw error;
    }
  }
};

// Utility functions
export const utils = {
  // Format currency (default to INR)
  formatCurrency: (amount, currency = 'INR') => {
    if (currency === 'USD') {
      return `$${amount.toLocaleString('en-US')}`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  },

  // Format percentage
  formatPercentage: (value) => {
    return `${Math.round(value)}%`;
  },

  // Validate email
  validateEmail: (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  },

  // Validate phone
  validatePhone: (phone) => {
    const re = /^\+?[1-9]\d{1,14}$/;
    return re.test(phone);
  },

  // Generate unique ID
  generateId: () => {
    return Math.random().toString(36).substr(2, 9);
  },

  // Debounce function
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
};

// Resume Builder API
export const resumeAPI = {
  // Generate PDF resume and trigger download
  generatePdf: async (resumeData) => {
    try {
      const response = await api.post('/api/resume/generate-pdf', resumeData, {
        responseType: 'blob',
        timeout: 60000 // 60 seconds for PDF generation
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Resume.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      console.error('Resume PDF generation error:', error);
      throw error;
    }
  },

  // Get resume builder usage stats
  getUsage: async () => {
    try {
      const response = await api.get('/api/resume/usage');
      return response.data;
    } catch (error) {
      console.error('Error fetching resume usage:', error);
      // Return default for non-authenticated users
      return { used: 0, limit: 2, unlimited: false, remaining: 2, tier: 'free' };
    }
  },

  // Tailor resume using AI based on target role or job description
  tailorResume: async (resumeData) => {
    try {
      const response = await api.post('/api/resume/tailor', resumeData, {
        timeout: 60000 // 60 seconds for AI processing
      });
      return response.data;
    } catch (error) {
      console.error('Resume tailoring error:', error);
      throw error;
    }
  }
};

// Blog API functions
export const blogAPI = {
  // Get all published blog posts (paginated)
  getAllPosts: async (page = 1, limit = 10) => {
    try {
      const response = await api.get('/api/blog/posts', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      throw error;
    }
  },

  // Get single blog post by slug
  getPostBySlug: async (slug) => {
    try {
      const response = await api.get(`/api/blog/posts/${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching blog post:', error);
      throw error;
    }
  }
};

// Newsletter API functions
export const newsletterAPI = {
  // Subscribe to newsletter
  subscribe: async (email, source = 'popup') => {
    try {
      const response = await api.post('/api/newsletter/subscribe', {
        email,
        source
      });
      return response.data;
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }
};

// Features API functions
export const featuresAPI = {
  // AI Career Coach chat
  coachChat: async (message, context = {}) => {
    try {
      const response = await api.post('/api/features/coach/chat', {
        message,
        context,
      });
      return response.data;
    } catch (error) {
      console.error('Error in coach chat:', error);
      throw error;
    }
  },

  // Weekly challenges
  getChallenges: async (careerPath = null) => {
    try {
      const params = careerPath ? { career_path: careerPath } : {};
      const response = await api.get('/api/features/challenges', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching challenges:', error);
      throw error;
    }
  },

  completeChallenge: async (challengeId, xpEarned = 0) => {
    try {
      const response = await api.post('/api/features/challenges/complete', {
        challenge_id: challengeId,
        xp_earned: xpEarned,
      });
      return response.data;
    } catch (error) {
      console.error('Error completing challenge:', error);
      throw error;
    }
  },

  // Career readiness score
  getReadinessScore: async () => {
    try {
      const response = await api.get('/api/features/readiness-score');
      return response.data;
    } catch (error) {
      console.error('Error fetching readiness score:', error);
      throw error;
    }
  },

  // Trending careers (public)
  getTrendingCareers: async () => {
    try {
      const response = await api.get('/api/features/trending-careers');
      return response.data;
    } catch (error) {
      console.error('Error fetching trending careers:', error);
      throw error;
    }
  },

  // Referral program
  createReferralLink: async (referrerName = null) => {
    try {
      const response = await api.post('/api/features/referral/create', {
        referrer_name: referrerName,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating referral link:', error);
      throw error;
    }
  },

  getReferralStats: async () => {
    try {
      const response = await api.get('/api/features/referral/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching referral stats:', error);
      throw error;
    }
  },

  trackReferral: async (referralCode) => {
    try {
      const response = await api.post(`/api/features/referral/track?referral_code=${referralCode}`);
      return response.data;
    } catch (error) {
      console.error('Error tracking referral:', error);
      throw error;
    }
  },

  // Peer comparison
  getPeerComparison: async () => {
    try {
      const response = await api.get('/api/features/peer-comparison');
      return response.data;
    } catch (error) {
      console.error('Error fetching peer comparison:', error);
      throw error;
    }
  },

  // Email-gated report
  requestReport: async (email, careerData = null) => {
    try {
      const response = await api.post('/api/features/report/request', {
        email,
        career_data: careerData,
      });
      return response.data;
    } catch (error) {
      console.error('Error requesting report:', error);
      throw error;
    }
  },

  // Journey stats
  getJourneyStats: async () => {
    try {
      const response = await api.get('/api/features/journey-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching journey stats:', error);
      throw error;
    }
  },

  // Skill radar
  getSkillRadar: async (roadmapId = null) => {
    try {
      const params = roadmapId ? { roadmap_id: roadmapId } : {};
      const response = await api.get('/api/features/skill-radar', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching skill radar:', error);
      throw error;
    }
  },
};

// ── Credentials / Micro-Certifications API ─────────────────────────────
export const credentialsAPI = {
  generateQuiz: async (roadmapId, weekIndex) => {
    try {
      const response = await api.post('/api/credentials/generate-quiz', {
        roadmap_id: roadmapId,
        week_index: weekIndex,
      });
      return response.data;
    } catch (error) {
      console.error('Error generating quiz:', error);
      throw error;
    }
  },

  submitQuiz: async (quizId, roadmapId, weekIndex, answers, metadata = {}) => {
    try {
      const response = await api.post('/api/credentials/submit-quiz', {
        quiz_id: quizId,
        roadmap_id: roadmapId,
        week_index: weekIndex,
        answers,
        time_taken_seconds: metadata.timeTaken || 0,
        integrity_flags: metadata.integrityFlags || 0,
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting quiz:', error);
      throw error;
    }
  },

  getMyCredentials: async () => {
    try {
      const response = await api.get('/api/credentials/my-credentials');
      return response.data;
    } catch (error) {
      console.error('Error fetching credentials:', error);
      throw error;
    }
  },

  // Public — no auth needed
  verifyCredential: async (credentialId) => {
    try {
      const response = await api.get(`/api/credentials/verify/${credentialId}`);
      return response.data;
    } catch (error) {
      console.error('Error verifying credential:', error);
      throw error;
    }
  },

  getMyAttempts: async () => {
    try {
      const response = await api.get('/api/credentials/my-attempts');
      return response.data;
    } catch (error) {
      console.error('Error getting attempts:', error);
      throw error;
    }
  },
};

export const userAPI = {
  getProfile: async () => {
    try {
      const response = await api.get('/api/user/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.post('/api/user/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }
};

export const b2bAPI = {
  createOrganization: async (data) => {
    try {
      const response = await api.post('/api/b2b/organization', data);
      return response.data;
    } catch (error) {
      console.error('Error creating B2B org', error); throw error;
    }
  },
  createCohort: async (data) => {
    try {
      const response = await api.post('/api/b2b/cohort', data);
      return response.data;
    } catch (error) {
      console.error('Error creating B2B cohort', error); throw error;
    }
  },
  getOrgStats: async (orgId) => {
    try {
      const response = await api.get(`/api/b2b/organization/${orgId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching org stats', error); throw error;
    }
  }
};

export default api;